import { db } from '../config/firebase';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getAuth, updateProfile } from "firebase/auth";

/**
 * Handles user creation or updates user's last login time in Firestore.
 * Retrieves the user document after creation or update.
 * @param {object} authUser - The user object from Firebase Authentication.
 * @returns {object} The user data from Firestore.
 */
export const handleUserLogin = async (authUser) => {
  if (!authUser || !authUser.uid) {
    console.error("Invalid authUser object provided to handleUserLogin.");
    throw new Error("Auth user is invalid.");
  }
  const userRef = doc(db, "users", authUser.uid);
  let userDataForReturn;

  try {
    const docSnap = await getDoc(userRef);    if (docSnap.exists()) {
      const existingData = docSnap.data();
      const updateData = {
        lastLoginAt: serverTimestamp(),
        email: authUser.email,
        name: authUser.displayName || existingData.name || "Adventurer",
        photoURL: authUser.photoURL || existingData.photoURL || "",
      };
      await updateDoc(userRef, updateData);
      userDataForReturn = { 
        uid: authUser.uid, 
        ...existingData,
        ...updateData,
        lastLoginAt: new Date()
      };
    } else {
      const newUserPayload = {
        uid: authUser.uid,
        email: authUser.email,
        name: authUser.displayName || "Adventurer",
        photoURL: authUser.photoURL || "",
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        roles: { admin: false, dungeonMaster: false, elevated: false },
        authenticated: false,
        availableDays: [],
        vetoDay: null,
        preferredDays: [],
      };
      await setDoc(userRef, newUserPayload);
      userDataForReturn = {
        ...newUserPayload,
        // For this return, we'll assume the structure is what we set.
        // The actual server timestamps will be in Firestore.
      };
    }
    return userDataForReturn;
  } catch (error) {
    console.error("Error in handleUserLogin:", error);
    throw error;
  }
};

/**
 * Updates the user's ultra authentication status in Firestore.
 * @param {string} uid - The user's unique ID.
 * @param {boolean} status - The new ultra authentication status.
 */
export const setUltraAuthenticatedStatus = async (uid, status) => {
  if (!uid) {
    console.error("User UID is required to update authentication status.");
    throw new Error("User UID is required.");
  }
  const userRef = doc(db, "users", uid);
  try {
    await updateDoc(userRef, {
      authenticated: status,
    });
    console.log(`User ${uid} ultra authentication status in Firestore set to ${status}`);
  } catch (error) {
    console.error("Error updating ultra authentication status in Firestore:", error);
    throw error;
  }
};

/**
 * Updates the user's D&D session availability preferences in Firestore.
 * @param {string} uid - The user's unique ID.
 * @param {Array<string>} availableDays - An array of strings representing available days.
 * @param {string|null} vetoDay - A string representing the vetoed day, or null.
 * @param {Array<string>} preferredDays - An array of strings representing top 3 preferred days in order.
 */
export const updateUserAvailability = async (uid, availableDays, vetoDay, preferredDays) => {
  if (!uid) {
    console.error("User UID is required to update availability.");
    throw new Error("User UID is required.");
  }
  const userRef = doc(db, "users", uid);
  try {
    await updateDoc(userRef, {
      availableDays: availableDays,
      vetoDay: vetoDay,
      preferredDays: preferredDays, // Store preferred days
    });
    console.log(`User ${uid} availability and preferences updated in Firestore.`);
  } catch (error) {
    console.error("Error updating user availability in Firestore:", error);
    throw error;
  }
};

/**
 * Updates the user's profile name in Firestore and Firebase Authentication.
 * @param {string} uid - The user's unique ID.
 * @param {string} newName - The new display name for the user.
 */
export const updateUserProfileName = async (uid, newName) => {
  if (!uid) {
    console.error("User UID is required to update name.");
    throw new Error("User UID is required.");
  }
  if (!newName || newName.trim() === "") {
    console.error("New name cannot be empty.");
    throw new Error("New name cannot be empty.");
  }

  const trimmedNewName = newName.trim();
  const auth = getAuth();
  const currentUserAuth = auth.currentUser;

  try {
    // Step 1: Update Firebase Authentication display name (if it's the current user)
    if (currentUserAuth && currentUserAuth.uid === uid) {
      await updateProfile(currentUserAuth, { displayName: trimmedNewName });
      console.log(`Firebase Auth display name updated for user ${uid}.`);
    } else {
      console.warn(`Current auth user (uid: ${currentUserAuth?.uid}) does not match target uid (${uid}) for auth profile update, or no user is signed in. Firestore name will still be updated.`);
    }

    // Step 2: Update Firestore user document
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, {
      name: trimmedNewName,
      updatedAt: serverTimestamp(), 
    });
    console.log(`User ${uid} name updated to ${trimmedNewName} in Firestore.`);
  } catch (error) {
    console.error("Error updating user name (in Auth or Firestore):", error);
    throw error; // Re-throw to be caught by the calling function in AccountPage
  }
};

/**
 * Updates email notification settings for a user
 * @param {string} uid - User ID
 * @param {object} settings - Email notification settings object
 * @returns {Promise<void>}
 */
export const updateUserEmailNotificationSettings = async (uid, settings) => {
  if (!uid) {
    throw new Error("User ID is required to update email notification settings.");
  }

  const userRef = doc(db, "users", uid);
  try {
    await updateDoc(userRef, {
      emailNotificationSettings: settings,
      updatedAt: serverTimestamp(),
    });
    console.log(`Email notification settings updated for user ${uid}`);
  } catch (error) {
    console.error("Error updating email notification settings:", error);
    throw error;
  }
};

/**
 * Gets email notification settings for a user
 * @param {string} uid - User ID
 * @returns {Promise<object>} Email notification settings
 */
export const getUserEmailNotificationSettings = async (uid) => {
  if (!uid) {
    throw new Error("User ID is required to get email notification settings.");
  }

  const userRef = doc(db, "users", uid);
  try {
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      const userData = docSnap.data();
      return userData.emailNotificationSettings || { campaignEpisodeCreated: false };
    } else {
      console.log("User document not found");
      return { campaignEpisodeCreated: false };
    }
  } catch (error) {
    console.error("Error getting email notification settings:", error);
    throw error;
  }
};

/**
 * Gets all users who have email notifications enabled for campaign episodes
 * This function respects both user preferences AND admin settings
 * @returns {Promise<Array>} Array of user objects with email notification enabled
 */
export const getUsersWithCampaignEmailNotifications = async () => {
  try {
    const usersRef = collection(db, "users");
    // Get all users who have opted in to campaign episode notifications
    const q = query(usersRef, where("emailNotificationSettings.campaignEpisodeCreated", "==", true));
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      // Double-check that user has explicitly opted in (defensive programming)
      if (userData.emailNotificationSettings && userData.emailNotificationSettings.campaignEpisodeCreated === true) {
        users.push({
          uid: userData.uid,
          email: userData.email,
          name: userData.name || 'Adventurer'
        });
      }
    });
    
    return users;
  } catch (error) {
    console.error("Error getting users with campaign email notifications:", error);
    throw error;
  }
};

/**
 * Checks if the admin email notification system is enabled
 * @param {string} adminUid - The admin user's UID
 * @returns {Promise<boolean>} Whether admin has enabled the email system
 */
export const isAdminEmailNotificationSystemEnabled = async (adminUid) => {
  if (!adminUid) {
    return false;
  }

  try {
    const userRef = doc(db, "users", adminUid);
    const docSnap = await getDoc(userRef);
    
    if (docSnap.exists()) {
      const userData = docSnap.data();
      return userData.emailNotificationSettings?.adminSettings?.campaignEpisodeCreated === true;
    }
    
    return false;
  } catch (error) {
    console.error("Error checking admin email notification system status:", error);
    return false;
  }
};
